import { uniq } from "lodash";
import maxBy from "lodash/maxBy";
import { createSelector } from "reselect";

import {
  BillingDocumentType,
  InactiveIntegrationStatuses,
} from "constants/billingDocuments";
import { IntegrationType } from "constants/integrations";

import { EMPTY_ARRAY } from "lib";

import { resolveSaleStatus } from "lib/saleStatus";

import {
  billingDocumentIsApproved,
  billingDocumentIsPaid,
} from "lib/billingDocument";
import { isSaleUrl } from "lib/navigation";
import { dateTimeStringToDateTime, getDaysDifferent } from "lib/timeFormats";
import { resolveEffectiveVendorCommission } from "lib/vendorCommission";

import { getIsLivestockAgent } from "../auth";
import {
  selectAggridBusinessDataByBillingDocumentIdLookup,
  selectWarningsByBillingDocumentIdLookup,
} from "../billingDocuments";
import { getActiveBillingRunId } from "../billingRuns";
import { getCurrentDeploymentSalesList } from "../currentSale";
import { getActiveLivestockAgentDeployment } from "../deploymentActiveAgent";
import {
  selectConsignmentIdsByDeploymentSaleIdLookup,
  selectConsignmentIdsByVendorIdLookup,
} from "../indexes";
import { selectIntegrationBusinessesByBusinessIdByTypeLookup } from "../integrationBusinesses";
import { selectActiveInterestRateSettingsByBusinessIdLookup } from "../interest";
import { selectPaymentsByBillingDocumentIdLookup } from "../payments";
import {
  getActiveRole,
  getBillingDocuments,
  getConsignments,
  getEmails,
  getSales,
  getSaleStatuses,
} from "../root";
import {
  selectSaleVendorCommissionByDeploymentIdLookup,
  selectVendorCommissionBandsByBusinessIdLookup,
  selectVendorCommissionForCurrentSaleByBusinessIdLookup,
  selectVendorCommissionForCurrentSaleByDeploymentIdLookup,
} from "../vendorCommissions";
import { selectEffectiveSaleTitleBySaleIdLookup } from "../sales";

function showOnOutstandingSettlementReport(
  billingDocument,
  business,
  payments,
) {
  // Only type invoice.
  if (billingDocument.type !== BillingDocumentType.INVOICE) {
    return false;
  }
  // Filtered for Businesses that are included in the DINS grouping
  if (!business.hasDebtorInsurance) {
    return false;
  }

  // Exclude voided or deleted invoices
  if (InactiveIntegrationStatuses.includes(billingDocument.integrationStatus)) {
    return false;
  }

  // We want to show invoices that are either:
  // - Paid in the last 7 days
  // - Approved and unpaid
  const mostRecentPayment = maxBy(payments, "paymentDate");
  const paidDateStr = mostRecentPayment?.paymentDate;

  // Show invoices paid in the last 7 days
  if (paidDateStr && billingDocumentIsPaid(billingDocument, payments)) {
    const paidDate = dateTimeStringToDateTime(paidDateStr);
    const daysSincePaid = getDaysDifferent(new Date(), paidDate);
    if (daysSincePaid <= 7) {
      return true;
    }
  }

  // Show approved and unpaid invoices
  if (
    billingDocumentIsApproved(billingDocument) &&
    !billingDocumentIsPaid(billingDocument, payments)
  ) {
    return true;
  }

  return false;
}

const transformBillingDocumentDataForAggrid = (
  billingDocument,
  emailByIdLookup,
  warningsByBillingDocumentIdLookup,
  saleByIdLookup,
  consignmentByIdLookup,
  consignmentIdsByVendorId,
  vendorCommissionForCurrentSaleByDeploymentIdLookup,
  saleVendorCommissionByDeploymentIdLookup,
  vendorCommissionForCurrentSaleByBusinessIdLookup,
  vendorCommissionBandsByBusinessIdLookup,
  businessAggridDataByBillingDocumentByIdLookup,
  integrationBusinessesByTypeByBusinessIdLookup,
  activeInterestRateSettingsByBusinessIdLookup,
  paymentsByBillingDocumentIdLookup,
  activeLivestockAgentDeployment,
  isLivestockAgent,
  vendorNumberByBillingDocumentIdLookup,
  effectiveSaleTitleBySaleIdLookup,
  activeRole,
  saleStatusesByIdLookup,
) => {
  const { business, branch, tradingTerms, relationships } =
    businessAggridDataByBillingDocumentByIdLookup[billingDocument.id] || {};
  const xeroIntegrationBusiness =
    integrationBusinessesByTypeByBusinessIdLookup[IntegrationType.Xero]?.[
      business.id
    ]?.[0] || null;
  const interestSettings =
    activeInterestRateSettingsByBusinessIdLookup[business.id]?.[0] || {};

  const payments =
    paymentsByBillingDocumentIdLookup[billingDocument.id] || EMPTY_ARRAY;

  // Billing documents at the moment generally only have one related livestock sale, but if they have more, we only need to check the first one
  const livestockSale = saleByIdLookup[billingDocument.livestockSaleIds?.[0]];
  const warnings = warningsByBillingDocumentIdLookup[billingDocument.id];

  const vendorCommissionBands =
    vendorCommissionBandsByBusinessIdLookup[business.id] || [];

  // resolve the effective vendor commissions for the document.
  // can only be determined when you have sale specific information, skip when outside a sale.
  // Is only relevant to Livestock agents, skip when not an agent.
  //
  // Note - isSaleUrl() will NOT cause this selector to re-fire when it's response changes.
  // The intended use is only to determine if the effectiveVendorCommissions should be calculated WHEN the selector is running.
  // When moving in and out of a sale url, many of the dependent selectors will re-run,
  // triggering this one to re-select, so a "stale" output from isSaleUrl() is not a concern.
  let effectiveVendorCommissions = [];
  if (
    isSaleUrl() &&
    isLivestockAgent &&
    activeLivestockAgentDeployment?.id &&
    billingDocument.livestockSaleIds?.length === 1 &&
    livestockSale
  ) {
    const deploymentCommission =
      vendorCommissionForCurrentSaleByDeploymentIdLookup[
        activeLivestockAgentDeployment.id
      ];
    const saleCommission =
      saleVendorCommissionByDeploymentIdLookup[
        activeLivestockAgentDeployment.id
      ];
    const vendorCommission =
      vendorCommissionForCurrentSaleByBusinessIdLookup[business.id];

    // when no consignments, include the consignment=null iteration
    // to resolve the effective commission for vendor, in sale, for deployment
    const consignmentIds = consignmentIdsByVendorId[business.id] || [null];
    effectiveVendorCommissions = consignmentIds
      .map(consignmentId => {
        const consignment = consignmentByIdLookup[consignmentId];
        return resolveEffectiveVendorCommission({
          consignmentCommission: consignment?.vendorCommission,
          deploymentCommission,
          saleCommission,
          vendorCommission,
        });
      })
      .filter(Boolean);
  }

  billingDocument.warnings = warnings;
  const livestockSales =
    billingDocument.livestockSaleIds?.map(livestockSaleId => {
      const sale = saleByIdLookup[livestockSaleId];
      const saleTitle = effectiveSaleTitleBySaleIdLookup[livestockSaleId];

      if (!sale) {
        return { sale_title: saleTitle, statusLabel: null, statusColor: null };
      }

      const { label: statusLabel, color: statusColor } = resolveSaleStatus(
        sale,
        activeRole,
        saleStatusesByIdLookup,
      );

      return { ...sale, sale_title: saleTitle, statusLabel, statusColor };
    }) || [];

  return {
    billingDocument,
    business,
    branch,
    tradingTerms,
    relationships,
    emails:
      billingDocument.emailIds?.map(id => emailByIdLookup[id]) || EMPTY_ARRAY,
    interestSettings,
    livestockSales,
    payments,
    showOnOutstandingSettlementReport: showOnOutstandingSettlementReport(
      billingDocument,
      business,
      payments,
    ),
    effectiveVendorCommissions,
    vendorCommissionBands,
    vendorNumber:
      vendorNumberByBillingDocumentIdLookup[billingDocument.id] ?? null,
    warnings,
    xeroIntegrationBusiness,
  };
};

/**
 * Vendor number for each billing document, from consignments in the current
 * sale. RCTI: recipient; Invoice: issuer (normally none, as issuer is the buyer).
 */
export const selectVendorNumberByBillingDocumentIdLookup = createSelector(
  [
    getBillingDocuments,
    getConsignments,
    getCurrentDeploymentSalesList,
    selectConsignmentIdsByDeploymentSaleIdLookup,
    selectConsignmentIdsByVendorIdLookup,
  ],
  (
    billingDocumentByIdLookup,
    consignmentByIdLookup,
    deploymentSalesList,
    consignmentIdsByDeploymentSaleId,
    consignmentIdsByVendorId,
  ) => {
    const currentSaleConsignmentIds = new Set(
      deploymentSalesList?.flatMap(
        ds => consignmentIdsByDeploymentSaleId[ds.deployment_sale_id] || [],
      ),
    );
    return Object.fromEntries(
      Object.values(billingDocumentByIdLookup).map(doc => {
        const businessId =
          doc.type === BillingDocumentType.RCTI
            ? doc.recipientBusinessId
            : doc.issuerBusinessId;
        const idsInCurrentSale = (
          consignmentIdsByVendorId[businessId] || []
        ).filter(id => currentSaleConsignmentIds.has(id));
        const vendorNumbers = uniq(
          idsInCurrentSale
            .map(id => {
              const consignment = consignmentByIdLookup[id];
              const prefixCode = consignment?.livestock_agency_code || "";
              const vendorNumber = consignment?.vendorNumber || "";
              return `${prefixCode}${vendorNumber}`;
            })
            .filter(Boolean),
        );
        return [
          doc.id,
          vendorNumbers.length > 0 ? vendorNumbers.join(", ") : null,
        ];
      }),
    );
  },
);

export const selectBillingDocumentsAggridDataById = createSelector(
  [
    getBillingDocuments,
    getEmails,
    selectWarningsByBillingDocumentIdLookup,
    getSales,
    getConsignments,
    selectConsignmentIdsByVendorIdLookup,
    selectVendorCommissionForCurrentSaleByDeploymentIdLookup,
    selectSaleVendorCommissionByDeploymentIdLookup,
    selectVendorCommissionForCurrentSaleByBusinessIdLookup,
    selectVendorCommissionBandsByBusinessIdLookup,
    selectAggridBusinessDataByBillingDocumentIdLookup,
    selectIntegrationBusinessesByBusinessIdByTypeLookup,
    selectActiveInterestRateSettingsByBusinessIdLookup,
    selectPaymentsByBillingDocumentIdLookup,
    getActiveLivestockAgentDeployment,
    getIsLivestockAgent,
    selectVendorNumberByBillingDocumentIdLookup,
    selectEffectiveSaleTitleBySaleIdLookup,
    getActiveRole,
    getSaleStatuses,
  ],
  (
    billingDocumentByIdLookup,
    emailByIdLookup,
    warningsByBillingDocumentIdLookup,
    saleByIdLookup,
    consignmentByIdLookup,
    consignmentIdsByVendorId,
    vendorCommissionForCurrentSaleByDeploymentIdLookup,
    saleVendorCommissionByDeploymentIdLookup,
    vendorCommissionForCurrentSaleByBusinessIdLookup,
    vendorCommissionBandsByBusinessIdLookup,
    businessAggridDataByBillingDocumentByIdLookup,
    integrationBusinessesByTypeByBusinessIdLookup,
    activeInterestRateSettingsByBusinessIdLookup,
    paymentsByBillingDocumentIdLookup,
    activeLivestockAgentDeployment,
    isLivestockAgent,
    vendorNumberByBillingDocumentIdLookup,
    effectiveSaleTitleBySaleIdLookup,
    activeRole,
    saleStatusesByIdLookup,
  ) =>
    Object.values(billingDocumentByIdLookup).reduce((acc, billingDocument) => {
      acc[billingDocument.id] = transformBillingDocumentDataForAggrid(
        billingDocument,
        emailByIdLookup,
        warningsByBillingDocumentIdLookup,
        saleByIdLookup,
        consignmentByIdLookup,
        consignmentIdsByVendorId,
        vendorCommissionForCurrentSaleByDeploymentIdLookup,
        saleVendorCommissionByDeploymentIdLookup,
        vendorCommissionForCurrentSaleByBusinessIdLookup,
        vendorCommissionBandsByBusinessIdLookup,
        businessAggridDataByBillingDocumentByIdLookup,
        integrationBusinessesByTypeByBusinessIdLookup,
        activeInterestRateSettingsByBusinessIdLookup,
        paymentsByBillingDocumentIdLookup,
        activeLivestockAgentDeployment,
        isLivestockAgent,
        vendorNumberByBillingDocumentIdLookup,
        effectiveSaleTitleBySaleIdLookup,
        activeRole,
        saleStatusesByIdLookup,
      );
      return acc;
    }, {}),
);

export const getBillingDocumentsAggridDataById = billingDocumentId => state =>
  selectBillingDocumentsAggridDataById(state)[billingDocumentId];

export const selectBillingDocumentsAggridData = createSelector(
  [selectBillingDocumentsAggridDataById],
  billingDocumentsAggridDataById =>
    Object.values(billingDocumentsAggridDataById),
);

export const selectActiveBillingRunBillingDocumentsAggridData = createSelector(
  [selectBillingDocumentsAggridData, getActiveBillingRunId],
  (billingDocumentsAggriddata, activeBillingRunId) =>
    billingDocumentsAggriddata.filter(
      billingDocumentRow =>
        billingDocumentRow.billingDocument.billingRunId === activeBillingRunId,
    ),
);

export const selectActiveBillingRunWarnings = createSelector(
  [selectActiveBillingRunBillingDocumentsAggridData],
  billingDocumentsAggridData =>
    billingDocumentsAggridData.filter(doc => doc.warnings.length > 0),
);

export const selectEmailRecipientsByActiveBillingDocumentId = createSelector(
  [selectActiveBillingRunBillingDocumentsAggridData],
  billingDocumentRowData =>
    billingDocumentRowData.reduce((acc, { billingDocument, business }) => {
      acc[billingDocument.id] =
        business.emailRecipients?.filter(er => er.email) || EMPTY_ARRAY;
      return acc;
    }, {}),
);
